import { Component, TemplateFunction } from '@ribajs/core'
import {
  CustomerFavoritesService,
  ShopifyProductService,
} from '../services/index.js'

import type {
  CustomerFavorite,
  CustomerFavoriteProduct,
} from '../types/index.js'

/**
 * The types for the scope
 */
interface Scope {
  // PROPERTIES
  favorites: CustomerFavoriteProduct[]
  // METHODS
  // TEMPLATES
}

export class SbCustomerFavoritesComponent extends Component {
  public static tagName = 'sb-customer-favorites'

  public scope: Scope = {
    // OPTIONS
    favorites: [],
    // METHODS
    // TEMPLATES
  }

  /**
   * Attributes that can be passed to the custom element.
   * @note These are automatically added to the scope and parsed to string, number, boolean, object, etc
   */
  static get observedAttributes(): string[] {
    return []
  }

  /**
   * Attributes that must be passed to the custom element, if not all attributes are truthy,
   * the component will not be initialized and an error message will be issued.
   * @returns
   */
  protected requiredAttributes(): string[] {
    return []
  }

  /**
   * Called as soon as this custom element es registered as a available custom element
   */
  constructor() {
    super()
  }

  /**
   * Called as soon as this custom element es detected in the dom
   */
  protected async connectedCallback() {
    super.connectedCallback()
    this.init(SbCustomerFavoritesComponent.observedAttributes)
    this.refresh()
    this.addEventListeners()
  }

  /**
   * Called as soon as this element is removed from the DOM
   */
  protected disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListeners()
  }

  protected async refresh() {
    this.scope.favorites = await CustomerFavoritesService.getProducts()
  }

  /**
   * Called after this custom element is bound and ready
   */
  async afterBind() {
    await super.afterBind()
    this.classList.add(`${SbCustomerFavoritesComponent.tagName}-ready`)
  }

  protected getProductByHandle(handle: string) {
    return this.scope.favorites.find(product => {
      return product.handle === handle
    })
  }

  protected async onAdd(
    addedFavorite: CustomerFavorite,
    all: CustomerFavorite[],
  ) {
    this.debug('[SbCustomerFavoritesComponent] onAdd', addedFavorite, all)
    const found = this.getProductByHandle(addedFavorite.handle)
    if (found) {
      // Update variant, TODO: Allow to add multiple variants?
      found.data = addedFavorite.data
      return
    }
    this.scope.favorites.push({
      ...addedFavorite,
      product: (
        await ShopifyProductService.getPreparedProductData(
          addedFavorite.handle,
          addedFavorite.data.variantId,
        )
      ).product,
    } as CustomerFavoriteProduct)
  }

  protected async onRemove(
    removedFavorite: CustomerFavorite,
    all: CustomerFavorite[],
  ) {
    this.debug('[SbCustomerFavoritesComponent] onAdd', removedFavorite, all)
    const found = this.getProductByHandle(removedFavorite.handle)
    if (!found) {
      return
    }
    found.action = 'removed'
  }

  protected async onClear(all: CustomerFavorite[]) {
    this.debug('[SbCustomerFavoritesComponent] onClear', all)
    this.scope.favorites = []
  }

  protected async onChange(
    changedFavorite: CustomerFavorite | null,
    all: CustomerFavorite[],
  ) {
    this.debug('[SbCustomerFavoritesComponent] onChange', changedFavorite, all)
  }

  protected addEventListeners() {
    CustomerFavoritesService.events.on(
      CustomerFavoritesService.addEventName,
      this.onAdd,
      this,
    )
    CustomerFavoritesService.events.on(
      CustomerFavoritesService.removeEventName,
      this.onRemove,
      this,
    )
    CustomerFavoritesService.events.on(
      CustomerFavoritesService.clearEventName,
      this.onClear,
      this,
    )
    CustomerFavoritesService.events.on(
      CustomerFavoritesService.changedEventName,
      this.onChange,
      this,
    )
  }

  protected removeEventListeners() {
    CustomerFavoritesService.events.off(
      CustomerFavoritesService.addEventName,
      this.onAdd,
      this,
    )
    CustomerFavoritesService.events.off(
      CustomerFavoritesService.removeEventName,
      this.onRemove,
      this,
    )
    CustomerFavoritesService.events.off(
      CustomerFavoritesService.clearEventName,
      this.onClear,
      this,
    )
    CustomerFavoritesService.events.off(
      CustomerFavoritesService.changedEventName,
      this.onChange,
      this,
    )
  }

  /**
   * Returns the template, if null is returned, the child html is not replaced, otherwise the innerHTML is replaced
   */
  protected template(): ReturnType<TemplateFunction> {
    return null
  }
}
